import React, { useState } from 'react';
import { CLASSES, COMPANIONS } from '../mock';
import { Hammer, Home, Sword, Sparkles } from 'lucide-react';
import Recipes from '../components/Recipes';

const features = [
  { icon: Home, title: 'Housing', text: 'Build & customize your dream cottage.' },
  { icon: Hammer, title: 'Crafting', text: 'Forge weapons, brew potions, and more.' },
  { icon: Sword, title: 'Combat', text: 'Engage in tactical real-time battles.' },
  { icon: Sparkles, title: 'Looting', text: 'Hunt rare drops across the realm.' },
];

const CraftingPage = () => {
  const [selected, setSelected] = useState(0);
  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-12 text-center fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">GAMEPLAY</p>
          <h1 className="font-heading section-heading-white mt-3">HOUSING, CRAFTING, LOOTING</h1>
          <h1 className="font-heading section-heading-gold">YOU NAME IT !</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 mb-16">
          {CLASSES.map((c, i) => {
            const isActive = selected === i;
            return (
              <button
                key={c.name}
                onClick={() => setSelected(i)}
                data-testid={`crafting-class-${c.name.toLowerCase()}`}
                className={`char-card relative group rounded-xl p-6 flex flex-col items-center text-center border transition-all duration-300 ${
                  isActive
                    ? 'border-[#f5b400] bg-black/55'
                    : 'border-white/15 bg-black/35 hover:border-[#f5b400]/60'
                }`}
              >
                <div
                  className="absolute inset-x-6 -top-3 h-1 rounded-full"
                  style={{ background: c.color, opacity: isActive ? 1 : 0.5 }}
                />
                <img
                  src={c.img}
                  alt={c.name}
                  className={`w-40 h-40 md:w-52 md:h-52 object-contain ${i % 2 === 0 ? 'float-anim' : 'float-anim-2'}`}
                  draggable="false"
                />
                <h3 className="font-heading text-2xl text-white mt-2">{c.name.toUpperCase()}</h3>
                <p className="font-body text-sm text-white/70 mt-2">{c.description}</p>
                <span
                  className="mt-4 font-nav text-xs"
                  style={{ color: isActive ? '#f5b400' : '#ffffff99' }}
                >
                  {isActive ? 'SELECTED' : 'SELECT'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-24">
          {features.map(({ icon: Icon, title, text }, i) => (
            <div
              key={i}
              className="group rounded-xl border border-white/10 bg-black/40 p-6 hover:border-[#f5b400]/60 transition-colors"
            >
              <Icon className="text-[#f5b400] mb-3" size={28} />
              <h4 className="font-heading text-white text-xl">{title}</h4>
              <p className="font-body text-sm text-white/70 mt-2">{text}</p>
            </div>
          ))}
        </div>

        {/* Recipes */}
        <Recipes />

        {/* Companions */}
        <div className="mb-12 text-center">
          <h2 className="font-heading section-heading-white">MEET COMPANIONS</h2>
          <h2 className="font-heading section-heading-gold">FRIENDS AND FOES</h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 font-body">
            Choose your friends and defeat your enemies. Meet countless funny NPCs, cute pets and
            fierce enemies in the land of <span className="text-[#f5b400]">Eldoria</span>!
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          {COMPANIONS.map((c, i) => (
            <div
              key={i}
              className="group relative rounded-lg overflow-hidden border border-white/10 bg-black/40 p-4 flex flex-col items-center text-center hover:border-[#f5b400]/60 transition-colors"
            >
              <div className="relative w-32 h-32 flex items-center justify-center">
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      'radial-gradient(circle at center, rgba(245,180,0,0.55) 0%, rgba(245,180,0,0.18) 45%, rgba(245,180,0,0) 70%)',
                    filter: 'blur(8px)',
                  }}
                />
                <img
                  src={c.img}
                  alt={c.name}
                  className="relative w-32 h-32 object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]"
                  draggable="false"
                />
              </div>
              <span
                className={`mt-3 font-nav text-[10px] px-2 py-0.5 rounded-full ${
                  c.type === 'FOE'
                    ? 'bg-red-700/30 text-red-300 border border-red-500/40'
                    : c.type === 'PET'
                      ? 'bg-emerald-700/30 text-emerald-200 border border-emerald-500/40'
                      : c.type === 'FRIEND'
                        ? 'bg-blue-700/30 text-blue-200 border border-blue-500/40'
                        : 'bg-yellow-700/20 text-[#f5b400] border border-[#f5b400]/40'
                }`}
              >
                {c.type}
              </span>
              <h4 className="font-heading text-white text-sm mt-2 leading-tight">{c.name}</h4>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CraftingPage;
